import { Links } from "@modules/Navbar/components/Links";
import { NavbarModal } from "@modules/Navbar/components/NavbarModal";
import { ProductAddedDisplay } from "@modules/Navbar/components/ProductAddedDisplay";
import { SearchModal } from "@modules/Navbar/components/SearchModal";
import { useLocale } from "@modules/shared/hooks/useLocale";
import { useCart } from "@shopify/hydrogen-react";
import { Link, useLocation } from "@tanstack/react-router";
import { Button } from "@ui/components/ui/button";
import { buttonVariants } from "@ui/components/variants/buttonVariants";
import { cn } from "@ui/lib/utils";
import { Menu, Search, ShoppingCart } from "lucide-react";
import { Suspense, lazy, useState } from "react";
import i18n from "@modules/shared/localization/i18n";
import { ProfileButton } from "./components/ProfileButton";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import { useDebouncedFunction } from "@/modules/shared/hooks/useDebouncedFunction";

const NoticeMarqueeBanner = lazy(() =>
  import("@modules/Navbar/components/NoticeMarqueeBanner").then((mod) => ({
    default: mod.NoticeMarqueeBanner,
  })),
);

export const Navbar: React.FC = () => {
  const { pathname } = useLocation();
  const locale = useLocale();
  const { totalQuantity } = useCart();
  const [showNavbarModal, setShowNavbarModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [closeCartItemModal, cancelClosingCartItemModal] = useDebouncedFunction(
    () => {
      if (typeof globalThis.window !== "undefined") {
        globalThis.window.dispatchEvent(new CustomEvent("cart:modalClose"));
      }
    },
    3500,
  );

  // Close search when navigating to a new page
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (prevPathname !== pathname) {
    setPrevPathname(pathname);
    setShowSearchModal(false);
  }

  return (
    <>
      <Suspense fallback={null}>
        <NoticeMarqueeBanner noticeId="holiday-notice-december-2024" />
      </Suspense>
      <nav
        className={cn(
          "relative z-[2] font-secondary text-headline-tny text-text-2",
          "grid grid-cols-40-dynamic border-b border-border-5/50 max-laptop:grid-cols-40-fixed max-mobile:grid-cols-15 max-mobile:border-b-0",
        )}
      >
        <div className="col-start-2 col-end-[-2] flex items-center justify-between py-1 max-mobile:py-3">
          {/* Logo */}
          <Link
            to="/$locale"
            params={{ locale }}
            className="flex shrink-0 items-center"
            onClick={() => {
              setShowNavbarModal(false);
              setShowSearchModal(false);
            }}
          >
            <img
              src="/logo.svg"
              alt="Logo"
              width={70}
              height={40}
              className="text-primary dark:invert"
            />
          </Link>

          {/* Desktop navigation links */}
          <div
            className={cn(
              "flex items-center max-mobile:hidden",
              "dark:text-text-6 dark:[&>path]:fill-icon-7",
            )}
          >
            <Links />
          </div>

          {/* Desktop actions */}
          <div
            className={cn(
              "flex items-center gap-0.5 max-mobile:hidden",
              "dark:text-text-6 dark:[&>path]:fill-icon-7",
            )}
          >
            <Button
              dark="ghost"
              size="small"
              className="text-text-2 transition-colors duration-200 hover:text-text-1"
              onClick={() => setShowSearchModal(true)}
              aria-label={i18n.t("Navbar:searchButton")}
            >
              <Search size={18} aria-hidden="true" />
            </Button>
            <ProfileButton />
            <LanguageSwitcher />
            <div className="group relative transition-colors duration-200">
              <Link
                className={cn(
                  buttonVariants({ dark: "ghost" }),
                  "flex items-center gap-1.5 px-2.5 py-2 transition-colors duration-200 group-hover:text-text-1",
                  "dark:group-hover:text-text-7",
                )}
                to="/$locale/cart"
                params={{ locale }}
              >
                <ShoppingCart size={18} />
                <span className="font-primary text-micro font-bold tabular-nums">
                  {totalQuantity}
                </span>
              </Link>
              <ProductAddedDisplay
                cancelClosing={cancelClosingCartItemModal}
                closeModal={closeCartItemModal}
              />
            </div>
          </div>

          {/* Mobile actions */}
          <div className="flex items-center gap-1 text-text-1 mobile:hidden">
            <Button
              light="ghost"
              className="p-2 text-text-2 transition-colors duration-200"
              onClick={() => setShowSearchModal(true)}
              aria-label={i18n.t("Navbar:searchButton")}
            >
              <Search size={18} className="text-text-1" aria-hidden="true" />
            </Button>
            <div>
              <Link
                to="/$locale/cart"
                params={{ locale }}
                className={cn(
                  buttonVariants({ light: "ghost" }),
                  "flex items-center gap-1 px-2 py-2 text-text-1!",
                )}
              >
                <ShoppingCart size={18} className="text-text-1" />
                <span className="font-primary text-micro font-bold tabular-nums">
                  {totalQuantity}
                </span>
              </Link>
              <ProductAddedDisplay
                cancelClosing={cancelClosingCartItemModal}
                closeModal={closeCartItemModal}
              />
            </div>
            <Button
              light="ghost"
              className="p-2 text-text-1 transition-colors duration-200"
              onClick={() => {
                setShowNavbarModal(true);
              }}
              aria-label={i18n.t("Navbar:mobileMenu", "Navigation")}
            >
              <Menu size={18} className="text-text-1" aria-hidden="true" />
            </Button>
          </div>
        </div>
        <NavbarModal
          setShowNavigation={setShowNavbarModal}
          showNavigation={showNavbarModal}
        />
        {showSearchModal && (
          <SearchModal show={true} close={() => setShowSearchModal(false)} />
        )}
      </nav>
    </>
  );
};
